<script setup lang="ts">
defineOptions({ inheritAttrs: false })

import type { ActiveCall } from 'shared/src'
import { CallStatusSeverity } from 'shared/src'
import { useLiveAgents } from '@/composables/livesupervision/useLiveVirtualAgents'
import { formatLatencySeconds, useLivekit, useLivekitCore } from '@/composables/livekit'
import { useDebounceLock } from '@/composables/useDebounceLock'
import { useToast } from '@ai/ui-components/toast'
import {
  RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING,
  useToastViewportScope,
} from '@/composables/toast'
import { useOTranscriptContext } from '@/composables/transcripts/useOTranscriptContext'
import LiveCallDetailsPanel from '@/components/BusinessLogic/CallDetails/LiveCallDetailsPanel.vue'

const { t, locale } = useI18n()
const toast = useToast()

const ARBEO_TOOL_NAMES = new Set([
  'arbeo_init_session',
  'arbeo_get_next_question',
  'arbeo_submit_answer',
  'arbeo_end_session',
])

const formatToolLatencyMessage = (tool: string, latencyMs: number) =>
  t('arbeo_tool_latency_warning', {
    tool: ARBEO_TOOL_NAMES.has(tool) ? t(`arbeo_tool_${tool.slice('arbeo_'.length)}`) : tool,
    latency: formatLatencySeconds(latencyMs),
  })

const { withDebounce, isDebouncing } = useDebounceLock(500)
const { disconnectedRooms, connectionError } = useLivekitCore()

const props = defineProps<{
  item: ActiveCall
  calls: ActiveCall[]
  agentId: string
}>()

const panelOpen = ref(false)
const currentCallIndex = ref(0)
const isLoadingContext = ref(false)

useToastViewportScope(panelOpen, RIGHT_PANEL_TOAST_VIEWPORT_PRESET_FLOATING)

const { agentsList, currentAgent, nextAgent, prevAgent, currentAgentIndex } = useLiveAgents(
  locale.value,
  t,
)
const { connectToRoom, getRoomStatus, disconnectFromRoom, audio, context } = useLivekit({
  formatToolLatency: formatToolLatencyMessage,
})
const openPanel = async (roomName: string) => {
  const agentIdx = agentsList.value.findIndex((a) => a.id === props.agentId)
  if (agentIdx !== -1) currentAgentIndex.value = agentIdx

  currentCallIndex.value = props.calls.findIndex((c) => c.id === props.item.id)
  if (currentCallIndex.value === -1) currentCallIndex.value = 0

  await disconnectFromRoom(true)

  if (disconnectedRooms.value.has(roomName)) {
    toast.warning(t('call_details_available_in_cold_monitoring'))
    return
  }

  const roomStatus = await getRoomStatus(roomName)
  if (roomStatus === 'ended') {
    disconnectedRooms.value.add(roomName)
    isLoadingContext.value = false
    return
  }
  if (roomStatus === 'unavailable') {
    toast.error(t('livekit_connection_error'), {
      title: t('connection_error'),
    })
    isLoadingContext.value = false
    return
  }

  panelOpen.value = true
  isLoadingContext.value = true
  await connectToRoom({ roomName: roomName, autoSubscribe: false })
  isLoadingContext.value = false
}

const switchRoomToCurrentCall = async () => {
  isLoadingContext.value = true
  await disconnectFromRoom(true)

  const newRoomName = currentCall.value.roomName
  if (!newRoomName || !audio) {
    console.warn('⚠️ Missing data when switching room', { newRoomName })
    isLoadingContext.value = false
    return
  }

  const current = audio.currentListeningId.value

  if (current && current !== newRoomName) {
    void audio.toggleListening(current)
  }

  const roomStatus = await getRoomStatus(newRoomName)
  if (roomStatus === 'ended') {
    disconnectedRooms.value.add(newRoomName)
    isLoadingContext.value = false
    return
  }
  if (roomStatus === 'unavailable') {
    toast.error(t('livekit_connection_error'), {
      title: t('connection_error'),
    })
    isLoadingContext.value = false
    return
  }

  await connectToRoom({ roomName: newRoomName, autoSubscribe: false })
  isLoadingContext.value = false
}

const nextCall = () => {
  void withDebounce(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const calls = currentAgent.value.activeCalls ?? []
    const isLastCall = currentCallIndex.value >= calls.length - 1 && !hasNext.value
    if (isLastCall) return

    if (currentCallIndex.value < calls.length - 1) {
      currentCallIndex.value++
    } else {
      nextAgent()
      currentCallIndex.value = 0
    }

    void switchRoomToCurrentCall()
  })
}

const prevCall = () => {
  void withDebounce(() => {
    const isFirstCall = currentCallIndex.value <= 0 && !hasPrev.value
    if (isFirstCall) return

    if (currentCallIndex.value > 0) {
      currentCallIndex.value--
    } else {
      prevAgent()
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const prevCalls = currentAgent.value.activeCalls ?? []
      currentCallIndex.value = Math.max(prevCalls.length - 1, 0)
    }

    void switchRoomToCurrentCall()
  })
}

const currentCall = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const calls = currentAgent.value.activeCalls ?? []
  return calls[currentCallIndex.value] ?? calls[0]
})
const currentRoomName = computed(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  () => currentCall.value.roomName ?? '',
)
const isCurrentCallDisconnected = computed(
  () => Boolean(currentRoomName.value) && disconnectedRooms.value.has(currentRoomName.value),
)

defineExpose({ openPanel })

const hasNext = computed(() => {
  for (let i = currentAgentIndex.value + 1; i < agentsList.value.length; i++) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if ((agentsList.value[i].activeCalls.length ?? 0) > 0) return true
  }
  return false
})

const hasPrev = computed(() => {
  for (let i = currentAgentIndex.value - 1; i >= 0; i--) {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if ((agentsList.value[i].activeCalls.length ?? 0) > 0) return true
  }
  return false
})

const transcriptLiveAudio = computed(() => ({
  mode: 'live' as const,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  durationSec: currentCall.value.durationSec ?? 0,
  isListening: listeningAgentId.value === currentCall.value.roomName,
  canListen:
    Boolean(currentCall.value.roomName) && !disconnectedRooms.value.has(currentCall.value.roomName),
}))
const transcriptContext = useOTranscriptContext({
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  transcripts: () => context.transcripts.value ?? [],
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  events: () => context.tools.value ?? [],
  audio: transcriptLiveAudio,
})

const openDisconnectModal = ref(false)
const toggleModal = () => {
  openDisconnectModal.value = !openDisconnectModal.value
}

const listeningAgentId = computed(() => audio?.currentListeningId.value ?? null)
const currentCallStatus = computed(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  () => currentCall.value.status ?? [],
)
const currentCallStatusValue = computed(() => currentCallStatus.value.at(-1))
const currentCallStatusColor = computed(() => {
  const status = currentCallStatusValue.value
  return status && CallStatusSeverity[status] > 0 ? 'warning' : 'success'
})
const currentCallStatusLabel = computed(() => {
  const status = currentCallStatusValue.value
  return status && CallStatusSeverity[status] > 0 ? t(status) : t('OK')
})
const toggleTranscriptLiveListening = () => {
  const roomName = currentCall.value.roomName
  if (!roomName) return
  void audio?.toggleListening(roomName, true)
}

const onEndCallSuccess = () => {
  void disconnectFromRoom(false, true)
}

watch(panelOpen, () => {
  if (!panelOpen.value) {
    void disconnectFromRoom(true)
    isLoadingContext.value = false
  }
})

// Context can arrive before connectToRoom() finishes its full connection/context workflow.
// Reveal the review surface as soon as transcript data is usable instead of blocking on that promise.
watch(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  () => context.transcripts.value ?? [],
  (transcripts) => {
    if (transcripts.length > 0 || disconnectedRooms.value.has(currentCall.value.roomName)) {
      isLoadingContext.value = false
    }
  },
  { deep: true },
)

// A call can end while context is loading. Stop the spinner immediately and preserve
// the end-of-call notification independently from the connection promise.
watch(
  () => disconnectedRooms.value.has(currentCall.value.roomName),
  (isDisconnected, wasDisconnected) => {
    if (isDisconnected) {
      isLoadingContext.value = false
      if (!wasDisconnected) {
        toast.warning(t('call_details_available_in_cold_monitoring'))
      }
    }
  },
)

watch(connectionError, (hasError) => {
  if (hasError && panelOpen.value) {
    toast.error(t('livekit_connection_error'), {
      title: t('connection_error'),
    })
  }
})
</script>

<template>
  <LiveCallDetailsPanel
    v-model:open="panelOpen"
    :call="currentCall"
    :agent-name="currentAgent.name"
    :status-color="currentCallStatusColor"
    :status-label="currentCallStatusValue ? currentCallStatusLabel : undefined"
    :left-disabled="isDebouncing || (!hasPrev && currentCallIndex <= 0)"
    :right-disabled="
      isDebouncing || (!hasNext && currentCallIndex >= (currentAgent.activeCalls.length ?? 1) - 1)
    "
    :loading="isLoadingContext"
    :disconnected="isCurrentCallDisconnected"
    :listening="listeningAgentId === currentCall.roomName"
    :audio="transcriptContext.audio.value"
    :transcript="transcriptContext.transcript.value"
    :events="transcriptContext.events.value"
    @navigate="(direction) => (direction === 'left' ? prevCall() : nextCall())"
    @toggle-listening="toggleTranscriptLiveListening"
    @disconnect="toggleModal"
  >
    <template #audio-renderer>
      <LivekitAudioPlayer v-if="audio" :audio="audio" />
    </template>
  </LiveCallDetailsPanel>
  <DisconnectVaModal v-model:open="openDisconnectModal" @end-success="onEndCallSuccess" />

  <slot :handle-click="openPanel" />
</template>
